import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Code2, CreditCard, LifeBuoy, LogOut, Moon, Settings, Sun } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { getCurrentUser, getUserDisplayName, getUserEmail } from '../../utils/auth';
import { SupportDialog } from '../support/SupportDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';

function initials(name: string) {
  return name.split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('') || '?';
}

interface AvatarMenuProps {
  compact?: boolean;
  side?: 'top' | 'bottom' | 'right';
  /** Shown under the name: Direction B puts the balance and this video's credits here. */
  extra?: ReactNode;
  /** Studio B's rail: day/night lives here, so no page needs a top bar for it. */
  theme?: boolean;
  /** Studio B's rail: the menu opens to the right as its own card, this many px off the trigger, clear of the rail. */
  beside?: number;
  /** Studio B's rail: one row open or collapsed, so the avatar never moves; the rail clips the row and the name fades. */
  wipe?: boolean;
}

const CARD = 'w-60 rounded-xl border-0 bg-b-elevated p-1.5 shadow-[0_0_0_1px_hsl(var(--b-line-strong)),0_2px_6px_rgb(23_18_33/0.06),0_24px_48px_-12px_rgb(23_18_33/0.32)] dark:shadow-[0_0_0_1px_hsl(var(--b-line-strong)),0_24px_48px_-12px_rgb(0_0_0/0.75)]';

export function AvatarMenu({ compact = false, side = compact ? 'bottom' : 'top', extra, theme = false, beside, wipe = false }: AvatarMenuProps) {
  const { user, signOut } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showSupport, setShowSupport] = useState(false);
  const info = getCurrentUser();
  const name = getUserDisplayName(info);
  const email = getUserEmail(info);

  const avatar = user?.photoURL ? (
    <img src={user.photoURL} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" />
  ) : (
    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-bold text-primary">{initials(name)}</span>
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Account menu"
            className={cn(
              'flex min-w-0 items-center gap-2.5 rounded-lg text-left transition-colors hover:bg-accent hover:text-accent-foreground',
              wipe ? 'w-full overflow-hidden py-2 pl-2.5' : compact ? 'p-0.5' : 'w-full px-1.5 py-2',
            )}
          >
            {avatar}
            {(wipe || !compact) && (
              <span data-rail-label={wipe || undefined} className={cn('min-w-0 pr-1', wipe ? 'w-[164px] shrink-0' : 'flex-1')}>
                <span className="block truncate text-sm font-medium" title={name}>{name}</span>
                <span className="block truncate text-xs text-muted-foreground" title={email}>{email}</span>
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={beside == null && side === 'top' ? 'start' : 'end'}
          side={beside == null ? side : 'right'}
          sideOffset={beside}
          className={beside == null ? 'w-56' : CARD}
        >
          <DropdownMenuLabel className="font-normal">
            <span className="block truncate text-sm font-medium">{name}</span>
            <span className="block truncate text-xs text-muted-foreground">{email}</span>
          </DropdownMenuLabel>
          {extra ? <div className="px-2 pb-2">{extra}</div> : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/settings/profile"><Settings className="mr-2 h-4 w-4" />Settings</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings/billing"><CreditCard className="mr-2 h-4 w-4" />Billing &amp; credits</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings/api"><Code2 className="mr-2 h-4 w-4" />API &amp; connector</Link>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setShowSupport(true)}>
            <LifeBuoy className="mr-2 h-4 w-4" />Help &amp; support
          </DropdownMenuItem>
          {theme && (
            <DropdownMenuItem onSelect={toggleTheme}>
              {isDarkMode ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
              {isDarkMode ? 'Light mode' : 'Dark mode'}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={async () => {
              await signOut();
              navigate({ to: '/login' });
            }}
          >
            <LogOut className="mr-2 h-4 w-4" />Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <SupportDialog open={showSupport} onOpenChange={setShowSupport} />
    </>
  );
}
